import { Link } from 'react-router';
import { Reveal } from '@components/motion';

const PRODUCT_SHOT = '/landing/innovations/contract-card.webp';
const ABOUT_IMG = '/landing/innovations/aid-photo.webp';

const ABOUT_1 =
    'This is one of the tools Citizens’ Gavel builds and runs directly. It exists because a specific step in getting justice was breaking down in the same way, over and over — and no amount of goodwill was going to fix it by hand at the scale it was happening.';
const ABOUT_2 =
    'It is designed to be used by people with no legal training, in low-bandwidth conditions, on the phones they already own. Where a question genuinely needs a lawyer, it says so and hands over to one rather than guessing.';

/* ── Product hero ─────────────────────────────────────────────────────── */

/**
 * "Get Started" — the product's own site opens in a new tab; a product with no
 * public app yet points somewhere internal, so that stays an in-app link.
 */
function GetStarted({ href, className }: { href: string; className: string }) {
    return /^https?:/.test(href) ? (
        <a href={href} target="_blank" rel="noopener noreferrer" className={className}>
            Get Started
        </a>
    ) : (
        <Link to={href} className={className}>
            Get Started
        </Link>
    );
}

export function InnovationHero({
    titleLead,
    titleBold,
    intro,
    logo,
    label,
    href,
}: {
    titleLead: string;
    titleBold: string;
    intro: string;
    logo: string;
    label: string;
    /** Where the product actually lives — the same destination as the band below. */
    href: string;
}) {
    return (
        <section className="relative overflow-hidden bg-gradient-to-b from-[#fbf7f4] to-[#f6ece3]">
            <div className="mx-auto flex max-w-[1640px] flex-col gap-10 px-5 pb-16 pt-[130px] lg:flex-row lg:items-center lg:gap-16 lg:px-[140px] lg:pb-24 lg:pt-[190px]">
                <div className="lg:flex-1">
                    <span className="mb-6 inline-flex items-center gap-3 rounded-full bg-white/80 py-2 pl-2 pr-5 shadow-[0_6px_20px_-12px_rgba(0,0,0,0.4)]">
                        <img src={logo} alt="" className="size-9 shrink-0 object-contain" />
                        <span className="text-[14px] font-bold text-[#08080c]">{label}</span>
                    </span>
                    <h1 className="font-primary text-[46px] font-bold leading-[1.06] tracking-[-2px] lg:text-[76px] lg:tracking-[-3px]">
                        <span className="text-[#a0a3a5]">{titleLead} </span>
                        <span className="text-[#08080c]">{titleBold}</span>
                        <span className="text-[#f60]">.</span>
                    </h1>
                    <p className="mt-6 max-w-[440px] text-[14px] leading-[1.7] text-[#333] lg:text-[15px]">{intro}</p>
                    <div className="mt-8 flex flex-wrap items-center gap-4">
                        <GetStarted
                            href={href}
                            className="inline-flex items-center justify-center rounded-[8px] bg-[#f60] px-7 py-3 text-[14px] font-bold text-white transition-transform duration-300 hover:scale-[1.03]"
                        />
                        <a
                            href="#about"
                            className="inline-flex items-center justify-center rounded-[8px] border border-[#08080c] px-7 py-3 text-[14px] font-bold text-[#08080c]"
                        >
                            Learn More
                        </a>
                    </div>
                </div>

                <Reveal as="div" once variant="fade-up" className="lg:w-[560px] lg:shrink-0">
                    <img src={PRODUCT_SHOT} alt="" className="w-full object-contain" />
                </Reveal>
            </div>
        </section>
    );
}

/* ── "About Citizens' Gavel <product>" band ───────────────────────────── */

export function InnovationAbout({ label }: { label: string }) {
    return (
        <section id="about" className="bg-white py-16 lg:py-24">
            <div className="mx-auto max-w-[1640px] px-5 lg:px-[140px]">
                <div className="flex flex-col gap-10 lg:flex-row lg:items-center lg:gap-20">
                    <div className="lg:flex-1">
                        <h2 className="font-primary text-[32px] font-bold leading-[1.15] tracking-[-1px] lg:text-[44px]">
                            <span className="text-[#a0a3a5]">About Citizens&rsquo; Gavel </span>
                            <span className="text-[#08080c]">{label}</span>
                        </h2>
                        <p className="mt-6 text-[14px] leading-[1.8] text-[#444]">{ABOUT_1}</p>
                        <p className="mt-4 text-[14px] leading-[1.8] text-[#5e5e5e]">{ABOUT_2}</p>
                    </div>
                    <Reveal as="div" once variant="fade-up" className="lg:w-[520px] lg:shrink-0">
                        <div className="h-[240px] w-full overflow-hidden rounded-[14px] bg-[#d9d9d9] lg:h-[380px]">
                            <img src={ABOUT_IMG} alt="" className="size-full object-cover" />
                        </div>
                    </Reveal>
                </div>
            </div>
        </section>
    );
}

/* ── Feature grid ─────────────────────────────────────────────────────── */

const FEATURES: { title: string; body: string }[] = [
    {
        title: 'Faster case resolution',
        body: 'Requests are triaged the day they arrive, so a file reaches someone who can act on it in days rather than months.',
    },
    {
        title: 'Verified legal professionals',
        body: 'Every lawyer on the platform is checked against the roll before they can take a matter — you can confirm it yourself.',
    },
    {
        title: 'Transparent pricing',
        body: 'You see the fee before you book. Where you qualify for legal aid, the cost is nothing and we say so upfront.',
    },
    {
        title: 'Private by default',
        body: 'Case details are encrypted in transit and at rest, and are never shared outside the lawyer handling your matter.',
    },
    {
        title: 'Nationwide coverage',
        body: 'Built for all 36 states and the FCT, in plain language, and usable on a low-end phone with a poor connection.',
    },
    {
        title: 'A human at the end of it',
        body: 'Automated guidance stops where the law gets uncertain and hands you to a qualified person instead of guessing.',
    },
];

export function InnovationFeatures() {
    return (
        <section className="bg-[#f5f5f5] py-16 lg:py-24">
            <div className="mx-auto max-w-[1640px] px-5 lg:px-[140px]">
                <h2 className="font-primary text-[32px] font-bold tracking-[-1px] text-[#08080c] lg:text-[44px]">
                    What you get
                    <span className="text-[#f60]">.</span>
                </h2>
                <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                    {FEATURES.map((f, i) => (
                        <div key={f.title} className="rounded-[14px] bg-white p-7">
                            <span className="flex size-10 items-center justify-center rounded-full bg-[#fff1e6] text-[13px] font-bold text-[#f60]">
                                {String(i + 1).padStart(2, '0')}
                            </span>
                            <p className="mt-5 text-[17px] font-bold text-[#08080c]">{f.title}</p>
                            <p className="mt-2 text-[13px] leading-[1.7] text-[#5e5e5e]">{f.body}</p>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
}

/* ── Closing CTA ──────────────────────────────────────────────────────── */

/** `href` is the product's own site, or an internal path when it has no public app yet. */
const CTA_BUTTON =
    'inline-flex items-center justify-center rounded-[8px] bg-[#f60] px-8 py-3.5 text-[14px] font-bold text-white transition-transform duration-300 hover:scale-[1.03]';

export function InnovationCTA({ label, href }: { label: string; href: string }) {
    return (
        <section id="get-started" className="bg-white pb-16 lg:pb-24">
            <div className="mx-auto max-w-[1640px] px-5 lg:px-[140px]">
                <div className="flex flex-col items-start gap-6 rounded-[20px] bg-[#08080c] px-7 py-12 lg:flex-row lg:items-center lg:justify-between lg:px-16 lg:py-16">
                    <h2 className="font-primary max-w-[620px] text-[28px] font-bold leading-[1.2] tracking-[-1px] text-white lg:text-[40px]">
                        Ready to try {label}
                        <span className="text-[#f60]">?</span>
                    </h2>
                    <GetStarted href={href} className={CTA_BUTTON} />
                </div>
            </div>
        </section>
    );
}
